import { Plugin, PluginKey } from '@tiptap/pm/state';
import { EditorView } from '@tiptap/pm/view';
import { VueRenderer } from '@tiptap/vue-2';
import { computePosition, flip, offset, shift } from '@floating-ui/dom';
import MentionDetailsView from '@/components/tiptap/extensions/Mention/MentionDetailsView.vue';
import { Node as ProsemirrorNode } from '@tiptap/pm/model';
import store from '@/store';
import logger from '@/other/Logger';

type State = { isVisible: boolean };

class MentionDetailsPlugin {
  popup: HTMLElement | null = null;
  component: VueRenderer;
  node: ProsemirrorNode;
  element: HTMLElement;
  private onClickOutside: ((event: MouseEvent) => void) | null = null;

  constructor() {
    this.update();
  }

  update() {
    if (!this.element || !this.popup) return;
    this.updatePosition();
  }

  private updatePosition() {
    if (!this.element || !this.popup) return;

    const virtualEl = {
      getBoundingClientRect: () => this.element.getBoundingClientRect(),
    };

    computePosition(virtualEl, this.popup, {
      placement: 'bottom-start',
      middleware: [offset(8), flip(), shift({ padding: 8 })],
    }).then(({ x, y }) => {
      if (!this.popup) return;
      Object.assign(this.popup.style, {
        left: `${x}px`,
        top: `${y}px`,
      });
    });
  }

  show(userId: string, node: ProsemirrorNode, element: HTMLElement) {
    const user = store.state.companyUsers.find((user) => user.id === userId);

    if (!user) {
      logger.error('Mention details can not be shown: user not found #' + userId);
      return;
    }

    // Destroy any existing popup first
    this.destroy();

    const component = new VueRenderer(MentionDetailsView, {
      parent: null,
      propsData: { user },
    });

    this.component = component;
    this.element = element;
    this.node = node;

    const popup = document.createElement('div');
    popup.style.position = 'absolute';
    popup.style.zIndex = '9999';
    popup.appendChild(component.element);
    document.body.appendChild(popup);
    this.popup = popup;

    this.updatePosition();

    this.onClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (!popup.contains(target) && !element.contains(target)) {
        this.destroy();
      }
    };
    document.addEventListener('mousedown', this.onClickOutside);
  }

  destroy() {
    if (this.onClickOutside) {
      document.removeEventListener('mousedown', this.onClickOutside);
      this.onClickOutside = null;
    }

    if (this.popup) {
      this.popup.remove();
      this.popup = null;
    }

    if (this.component) {
      this.component.destroy();
    }
  }
}

let viewInstance: MentionDetailsPlugin = null;

const mentionDetailsPlugin = new Plugin<State>({
  key: new PluginKey('MentionDetails'),
  view() {
    viewInstance = new MentionDetailsPlugin();
    return viewInstance;
  },
  state: {
    init() {
      return { isVisible: false };
    },
    apply(tr, state) {
      return state;
    },
  },
  props: {
    handleClickOn(view: EditorView, pos: number, node: ProsemirrorNode, nodePos: number, event: MouseEvent) {
      if (node.type.name === 'mention') {
        const target = event.target as HTMLElement;
        const userId = target.dataset['id'];
        viewInstance.show(userId, node, target);
        return true;
      }
      return false;
    },
  },
});

export default mentionDetailsPlugin;
